/**
 * save-slot-picker.ts — overlay shown when "Save Game" is picked from
 * the in-game menu (v2M2). Lists all four slots with their current
 * contents and a Save / Overwrite action per row.
 *
 * For named slots the player is prompted for a label via window.prompt;
 * quicksave skips the prompt and stores `label: null`. Overwriting an
 * occupied named slot confirms first.
 *
 * Per UI_UX §9.3 the in-game menu remains open behind the picker — the
 * sim stays paused while the player decides. Picker closes back to the
 * menu on save-complete or cancel; main.ts decides whether to resume
 * after.
 */

import {
  serializeGameState,
  type SaveBlobHeader,
} from '../save/serialize';
import {
  ALL_SLOT_KEYS,
  type SaveSlotKey,
  type SaveStorage,
} from '../save/storage';
import type { GameState } from '../state/types';
import { getAudioEngine } from '../audio';

export interface SaveSlotPickerCallbacks {
  storage: SaveStorage;
  /**
   * Current GameState getter — re-evaluated at save time, not at picker
   * mount, so a save reflects the moment the player clicked Save.
   * (The sim is paused while the menu is open so the value is stable
   * either way, but the getter pattern future-proofs against the
   * picker becoming async.)
   */
  getState: () => GameState;
  /** Called after a successful save with the slot it landed in. */
  onSaved(slot: SaveSlotKey): void;
  /** Called when the player cancels without saving. */
  onCancel(): void;
}

export interface SaveSlotPickerHandle {
  destroy(): void;
}

const SLOT_DISPLAY_NAME: Readonly<Record<SaveSlotKey, string>> = {
  'slot:1': 'Slot 1',
  'slot:2': 'Slot 2',
  'slot:3': 'Slot 3',
  quicksave: 'Quicksave',
};

function formatRelativeTime(savedAtMs: number): string {
  const deltaSec = Math.max(0, Math.round((Date.now() - savedAtMs) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const deltaHr = Math.round(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr}h ago`;
  const deltaDay = Math.round(deltaHr / 24);
  return `${deltaDay}d ago`;
}

function describeOccupiedSlot(header: SaveBlobHeader): string {
  const label = header.label ?? 'Saved';
  return `${label} · Tick ${header.tick} · ${formatRelativeTime(header.savedAt)}`;
}

export function mountSaveSlotPicker(
  parent: HTMLElement,
  callbacks: SaveSlotPickerCallbacks,
): SaveSlotPickerHandle {
  const overlay = document.createElement('div');
  overlay.id = 'save-slot-picker-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(5, 6, 10, 0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1100',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);

  const card = document.createElement('div');
  Object.assign(card.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    minWidth: '480px',
    maxWidth: '600px',
    padding: '28px 32px',
    background: '#0E131C',
    border: '1px solid #2A3344',
    borderRadius: '2px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(card);

  const heading = document.createElement('div');
  heading.textContent = 'SAVE GAME';
  Object.assign(heading.style, {
    fontSize: '14px',
    letterSpacing: '0.18em',
    color: '#7E8799',
    marginBottom: '6px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(heading);

  function performSave(slot: SaveSlotKey): void {
    const isQuicksave = slot === 'quicksave';
    let label: string | null = null;
    if (!isQuicksave) {
      // Named slot — prompt for a label. The browser prompt blocks until
      // the user submits or cancels; OK with empty input keeps label null.
      const input = window.prompt(
        'Save label (optional):',
        '',
      );
      if (input === null) return; // cancel — leave picker open
      label = input.trim() === '' ? null : input.trim();
    }
    const blob = serializeGameState(callbacks.getState(), { label });
    callbacks.storage.write(slot, blob);
    callbacks.onSaved(slot);
  }

  function makeSlotRow(slot: SaveSlotKey, header: SaveBlobHeader | null): HTMLElement {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 14px',
      background: '#05060A',
      border: '1px solid #2A3344',
      borderRadius: '2px',
    } as Partial<CSSStyleDeclaration>);

    const left = document.createElement('div');
    Object.assign(left.style, {
      flex: '0 0 100px',
      fontWeight: '600',
      fontSize: '13px',
      letterSpacing: '0.06em',
    } as Partial<CSSStyleDeclaration>);
    left.textContent = SLOT_DISPLAY_NAME[slot];
    row.appendChild(left);

    const middle = document.createElement('div');
    Object.assign(middle.style, {
      flex: '1 1 auto',
      fontSize: '12px',
      color: header ? '#D7DEEA' : '#7E8799',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    } as Partial<CSSStyleDeclaration>);
    if (header) {
      const badge = makeOutcomeBadge(header.gameOutcome);
      if (badge) middle.appendChild(badge);
      const desc = document.createElement('span');
      desc.textContent = describeOccupiedSlot(header);
      middle.appendChild(desc);
    } else {
      middle.textContent = 'Empty';
    }
    row.appendChild(middle);

    const action = document.createElement('button');
    action.type = 'button';
    action.textContent = header ? 'Overwrite' : 'Save';
    Object.assign(action.style, {
      flex: '0 0 auto',
      padding: '8px 16px',
      background: '#2B4A7A',
      color: '#FFFFFF',
      border: '1px solid #3F6FA8',
      borderRadius: '2px',
      fontFamily: 'inherit',
      fontSize: '12px',
      letterSpacing: '0.06em',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    action.addEventListener('click', () => {
      if (header && !confirm(`Overwrite ${SLOT_DISPLAY_NAME[slot]}?`)) return;
      performSave(slot);
    });
    row.appendChild(action);

    return row;
  }

  // Render all four rows from the current slot state.
  for (const entry of callbacks.storage.listSlots()) {
    card.appendChild(makeSlotRow(entry.key, entry.header));
  }

  const footer = document.createElement('div');
  Object.assign(footer.style, {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '8px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(footer);

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.textContent = 'Cancel';
  Object.assign(cancelBtn.style, {
    padding: '10px 20px',
    background: '#0E131C',
    color: '#D7DEEA',
    border: '1px solid #2A3344',
    borderRadius: '2px',
    fontFamily: 'inherit',
    fontSize: '13px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  cancelBtn.addEventListener('click', callbacks.onCancel);
  footer.appendChild(cancelBtn);

  // Sanity-check that all 4 slot keys rendered, in case ALL_SLOT_KEYS
  // ever drifts from the SAVE_BLOB_VERSION-1 spec without updating this
  // file. Keeps a quick visual signal in dev.
  if (ALL_SLOT_KEYS.length !== 4) {
    console.warn('save-slot-picker: expected 4 slots, got', ALL_SLOT_KEYS.length);
  }

  parent.appendChild(overlay);
  getAudioEngine().playCue('ui:panel-open');
  return {
    destroy(): void {
      getAudioEngine().playCue('ui:panel-close');
      overlay.remove();
    },
  };
}

/**
 * v3M12.4 — small status badge for completed-game saves. Returns
 * `null` for in-progress games (no decoration). Per WIN_CONDITIONS
 * §7: green check for victory, red ✗ for defeat.
 */
function makeOutcomeBadge(
  outcome: 'in-progress' | 'victory' | 'defeat',
): HTMLElement | null {
  if (outcome === 'in-progress') return null;
  const badge = document.createElement('span');
  badge.textContent = outcome === 'victory' ? '✓' : '✗';
  Object.assign(badge.style, {
    color: outcome === 'victory' ? '#63C77C' : '#D24548',
    fontWeight: '700',
    fontSize: '14px',
    flex: '0 0 auto',
  } as Partial<CSSStyleDeclaration>);
  badge.title = outcome === 'victory' ? 'Victory' : 'Defeat';
  return badge;
}
